import React, { useEffect, useState } from "react";

export default function BusinessOperationsOverview() {
  const [loading, setLoading] = useState(true);

  const [primaryOrder] = useState({
    billing: 18.4,
    distributorOrders: 284,
    invoiceAmount: 12.8,
    pendingDispatch: 37,
    collectionsPending: 4.2,
    topDistributor: "Pune Traders",
    growth: 14,
    target: 22,
  });

  const [secondaryOrder] = useState({
    secondarySales: 14.2,
    retailerOrders: 482,
    productiveCalls: 316,
    strikeRate: 74,
    outletCoverage: 82,
    avgOrderValue: 2840,
    growth: 11,
    target: 18,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1800);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return <BusinessOperationsSkeleton />;
  }

  return (
    <>
      {/* SHIMMER */}

      <style>
        {`
          @keyframes shimmer {
            0% {
              background-position: -400px 0;
            }

            100% {
              background-position: 400px 0;
            }
          }

          @keyframes pulse {
            0% {
              opacity: 0.7;
            }

            50% {
              opacity: 1;
            }

            100% {
              opacity: 0.7;
            }
          }
        `}
      </style>

      {/* -------------------------------------------------------------------------- */}
      {/* PRIMARY ORDER */}
      {/* -------------------------------------------------------------------------- */}

      <SectionHeader icon="📦" title="Primary Order Overview" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "58% 42%",
          gap: 14,
          marginBottom: 28,
        }}
      >
        {/* LEFT */}

        <div>
          {/* HERO CARD */}

          <div
            style={{
              background: "linear-gradient(135deg,#10B981,#059669)",
              borderRadius: 20,
              padding: 22,
              color: "#fff",
              marginBottom: 14,
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 10px 25px rgba(16,185,129,0.18)",
            }}
          >
            {/* GLOW */}

            <div
              style={{
                position: "absolute",
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                top: -60,
                right: -40,
              }}
            />

            {/* TITLE */}

            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                opacity: 0.9,
                letterSpacing: "0.08em",
                marginBottom: 12,
                textTransform: "uppercase",
              }}
            >
              Today's Primary Billing
            </div>

            {/* VALUE */}

            <div
              style={{
                fontSize: 42,
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              ₹{primaryOrder.billing}Cr
            </div>

            {/* SUB */}

            <div
              style={{
                display: "flex",
                gap: 18,
                alignItems: "center",
                marginBottom: 22,
              }}
            >
              <TrendBadge value={primaryOrder.growth} />

              <div
                style={{
                  fontSize: 14,
                  opacity: 0.9,
                }}
              >
                Target: ₹{primaryOrder.target}Cr
              </div>
            </div>

            {/* PROGRESS */}

            <div
              style={{
                marginBottom: 10,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Achievement
                </span>

                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  83%
                </span>
              </div>

              <div
                style={{
                  height: 10,
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: "83%",
                    height: "100%",
                    background: "linear-gradient(90deg,#A7F3D0,#ECFDF5)",
                    borderRadius: 999,
                  }}
                />
              </div>
            </div>

            {/* FOOTER */}

            <div
              style={{
                marginTop: 18,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <HeroMini
                label="Distributor Orders"
                value={primaryOrder.distributorOrders}
              />

              <HeroMini
                label="Pending Dispatch"
                value={primaryOrder.pendingDispatch}
              />

              <HeroMini
                label="Collections"
                value={`₹${primaryOrder.collectionsPending}Cr`}
              />
            </div>
          </div>

          {/* KPI GRID */}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 12,
            }}
          >
            <StatCard
              label="Invoice Amount"
              value={`₹${primaryOrder.invoiceAmount}Cr`}
              accent="green"
              icon="🧾"
            />

            <StatCard
              label="Top Distributor"
              value={primaryOrder.topDistributor}
              accent="blue"
              icon="🏆"
            />

            <StatCard
              label="Dispatch Efficiency"
              value="92%"
              accent="amber"
              icon="🚚"
            />
          </div>
        </div>

        {/* RIGHT GRAPH */}

        <GraphContainer title="Primary Billing Trend">
          <ModernBarGraph
            data={[42, 68, 84, 58, 96, 74, 100]}
            color="#10B981"
          />

          <BottomMetrics
            items={[
              {
                label: "Avg Billing",
                value: "₹15.2Cr",
              },
              {
                label: "Peak Day",
                value: "Friday",
              },
              {
                label: "Growth",
                value: "+14%",
              },
            ]}
          />
        </GraphContainer>
      </div>

      {/* -------------------------------------------------------------------------- */}
      {/* SECONDARY ORDER */}
      {/* -------------------------------------------------------------------------- */}

      <SectionHeader icon="🚚" title="Secondary Order Overview" />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "58% 42%",
          gap: 14,
        }}
      >
        {/* LEFT */}

        <div>
          {/* HERO */}

          <div
            style={{
              background: "linear-gradient(135deg,#6366F1,#8B5CF6)",
              borderRadius: 20,
              padding: 22,
              color: "#fff",
              marginBottom: 14,
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 10px 25px rgba(99,102,241,0.18)",
            }}
          >
            {/* GLOW */}

            <div
              style={{
                position: "absolute",
                width: 180,
                height: 180,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                top: -60,
                right: -40,
              }}
            />

            {/* TITLE */}

            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                opacity: 0.9,
                letterSpacing: "0.08em",
                marginBottom: 12,
                textTransform: "uppercase",
              }}
            >
              Secondary Sales
            </div>

            {/* VALUE */}

            <div
              style={{
                fontSize: 42,
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              ₹{secondaryOrder.secondarySales}Cr
            </div>

            {/* SUB */}

            <div
              style={{
                display: "flex",
                gap: 18,
                alignItems: "center",
                marginBottom: 22,
              }}
            >
              <TrendBadge value={secondaryOrder.growth} />

              <div
                style={{
                  fontSize: 14,
                  opacity: 0.9,
                }}
              >
                Target: ₹{secondaryOrder.target}Cr
              </div>
            </div>

            {/* FUNNEL */}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 12,
              }}
            >
              <FunnelStep title="Visited" value="620" />

              <Arrow />

              <FunnelStep title="Ordered" value="482" />

              <Arrow />

              <FunnelStep title="Billed" value="316" />
            </div>
          </div>

          {/* KPI */}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 12,
            }}
          >
            <StatCard
              label="Strike Rate"
              value={`${secondaryOrder.strikeRate}%`}
              accent="amber"
              icon="🎯"
            />

            <StatCard
              label="Outlet Coverage"
              value={`${secondaryOrder.outletCoverage}%`}
              accent="blue"
              icon="📍"
            />

            <StatCard
              label="Avg Order Value"
              value={`₹${secondaryOrder.avgOrderValue}`}
              accent="green"
              icon="💵"
            />
          </div>
        </div>

        {/* RIGHT GRAPH */}

        <GraphContainer title="Retail Activity">
          <ModernLineGraph
            data={[28, 44, 62, 38, 76, 54, 88]}
            color="#6366F1"
          />

          <BottomMetrics
            items={[
              {
                label: "Productive Calls",
                value: "316",
              },
              {
                label: "Coverage",
                value: "82%",
              },
              {
                label: "Strike Rate",
                value: "74%",
              },
            ]}
          />
        </GraphContainer>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* SECTION HEADER */
/* -------------------------------------------------------------------------- */

function SectionHeader({ icon, title }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        marginTop: 24,
        paddingBottom: 10,
        borderBottom: "1px solid #E2E8F0",
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>

      <span
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "#334155",
        }}
      >
        {title}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* HERO MINI */
/* -------------------------------------------------------------------------- */

function HeroMini({ label, value }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          opacity: 0.8,
          marginBottom: 4,
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* TREND BADGE */
/* -------------------------------------------------------------------------- */

function TrendBadge({ value }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.14)",
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 700,
        backdropFilter: "blur(10px)",
        animation: "pulse 2s infinite",
      }}
    >
      ↑ {value}% Growth
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* KPI CARD */
/* -------------------------------------------------------------------------- */

function StatCard({ label, value, accent, icon }) {
  const accents = {
    green: {
      text: "#059669",
      border: "#A7F3D0",
    },

    amber: {
      text: "#D97706",
      border: "#FDE68A",
    },

    blue: {
      text: "#2563EB",
      border: "#BFDBFE",
    },
  };

  const c = accents[accent];

  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${c.border}`,
        borderRadius: 16,
        padding: 18,
        boxShadow: "0 2px 10px rgba(15,23,42,0.03)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#94A3B8",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </span>

        <span>{icon}</span>
      </div>

      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          color: c.text,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* GRAPH CONTAINER */
/* -------------------------------------------------------------------------- */

function GraphContainer({ title, children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        borderRadius: 18,
        padding: 18,
        boxShadow: "0 2px 10px rgba(15,23,42,0.03)",
      }}
    >
      <div
        style={{
          fontSize: 13,
          fontWeight: 700,
          color: "#334155",
          marginBottom: 22,
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>

      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MODERN BAR GRAPH */
/* -------------------------------------------------------------------------- */

function ModernBarGraph({ data, color }) {
  return (
    <div
      style={{
        height: 220,
        display: "flex",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 20,
      }}
    >
      {data.map((item, index) => (
        <div
          key={index}
          style={{
            flex: 1,
            height: `${item}%`,
            borderRadius: 14,
            background: color,
            opacity: 0.9,
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -28,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 11,
              fontWeight: 700,
              color: "#64748B",
            }}
          >
            {item}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MODERN LINE GRAPH */
/* -------------------------------------------------------------------------- */

function ModernLineGraph({ data, color }) {
  return (
    <div
      style={{
        height: 220,
        display: "flex",
        alignItems: "flex-end",
        gap: 12,
        marginBottom: 20,
      }}
    >
      {data.map((item, index) => (
        <div
          key={index}
          style={{
            flex: 1,
            height: `${item}%`,
            borderRadius: 999,
            background: color,
            position: "relative",
            opacity: 0.9,
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -28,
              left: "50%",
              transform: "translateX(-50%)",
              fontSize: 11,
              fontWeight: 700,
              color: "#64748B",
            }}
          >
            {item}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* BOTTOM METRICS */
/* -------------------------------------------------------------------------- */

function BottomMetrics({ items }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        borderTop: "1px solid #E2E8F0",
        paddingTop: 16,
      }}
    >
      {items.map((item, index) => (
        <div key={index}>
          <div
            style={{
              fontSize: 11,
              color: "#94A3B8",
              marginBottom: 4,
            }}
          >
            {item.label}
          </div>

          <div
            style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#334155",
            }}
          >
            {item.value}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* FUNNEL */
/* -------------------------------------------------------------------------- */

function FunnelStep({ title, value }) {
  return (
    <div
      style={{
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
        }}
      >
        {value}
      </div>

      <div
        style={{
          fontSize: 11,
          opacity: 0.8,
        }}
      >
        {title}
      </div>
    </div>
  );
}

function Arrow() {
  return (
    <div
      style={{
        fontSize: 18,
        opacity: 0.8,
      }}
    >
      →
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* SKELETON */
/* -------------------------------------------------------------------------- */

function BusinessOperationsSkeleton() {
  return (
    <div
      style={{
        height: 620,
        borderRadius: 18,
        background: "linear-gradient(90deg,#E2E8F0,#F8FAFC,#E2E8F0)",
        backgroundSize: "800px 100%",
        animation: "shimmer 1.5s infinite linear",
      }}
    />
  );
}
